/**
 * V2QsCollapsible 컴포넌트 Props 인터페이스
 * V1 QsCollapsible/QsCollapsibleGroup/QsCollapsibleGroupItem의 V2 버전 (순수 UI만)
 *
 * 접이식 체크박스 그룹 컴포넌트
 */

/**
 * 그룹 아이템 타입
 * - normal: 접이식 그룹 (펼침 화살표)
 * - treeSx: 단일 체크박스 아이템 (체크박스 + 라벨 + 버튼)
 * - noneAbove: "None of the above" 특수 옵션
 * - special: 기타 특수 옵션 (예: sexual assault)
 */
export type V2QsCollapsibleItemType = 'normal' | 'treeSx' | 'noneAbove' | 'special';

/**
 * 그룹 아이템
 */
export interface V2QsCollapsibleItem {
  /**
   * 아이템 키 (고유 식별자)
   */
  key: string;

  /**
   * 표시할 라벨
   */
  label: string;

  /**
   * 선택 여부
   */
  checked: boolean;

  /**
   * 답변이 있는지 (상세 정보 입력됨)
   */
  hasAnswer?: boolean;

  /**
   * 비활성화 여부
   */
  disabled?: boolean;
}

/**
 * 접이식 그룹
 */
export interface V2QsCollapsibleGroup {
  /**
   * 그룹 키 (고유 식별자)
   */
  key: string;

  /**
   * 그룹 라벨
   */
  label: string;

  /**
   * 그룹 타입
   */
  type: V2QsCollapsibleItemType;

  /**
   * 그룹 내 아이템 목록 (접이식 그룹의 경우)
   */
  items?: V2QsCollapsibleItem[];

  /**
   * 단일 선택 아이템인 경우 체크 상태
   */
  checked?: boolean;

  /**
   * 비활성화 여부
   */
  disabled?: boolean;
}

export interface V2QsCollapsibleProps {
  /**
   * 그룹 목록
   */
  groups: V2QsCollapsibleGroup[];

  /**
   * 현재 열린 그룹 키
   */
  openGroupKey?: string | null;

  /**
   * 그룹 열기/닫기
   */
  onGroupToggle?: (groupKey: string) => void;

  /**
   * 아이템 클릭 (상세 입력 모달 열기)
   */
  onItemClick?: (groupKey: string, itemKey: string) => void;

  /**
   * 아이템 체크 변경 (선택/해제)
   */
  onItemCheck?: (groupKey: string, itemKey: string, checked: boolean) => void;

  /**
   * 특수 옵션 체크 변경 (NoneAbove, Special 등)
   */
  onSpecialOptionCheck?: (groupKey: string, checked: boolean) => void;

  /**
   * 로딩 중인 그룹 키
   */
  loadingGroupKey?: string | null;

  /**
   * 비활성화 여부
   */
  disabled?: boolean;

  /**
   * 읽기 전용 여부
   */
  readOnly?: boolean;

  /**
   * 에러 상태
   */
  hasError?: boolean;

  /**
   * 에러 메시지
   */
  errorMessage?: string;

  // i18n 텍스트 Props
  /**
   * 로딩 텍스트
   */
  loadingText?: string;

  /**
   * 추가 버튼 텍스트
   */
  addButtonText?: string;

  /**
   * 수정 버튼 텍스트
   */
  editButtonText?: string;

  /**
   * 추가 CSS 클래스
   */
  className?: string;

  /**
   * 그룹 CSS 클래스
   */
  groupClassName?: string;

  /**
   * 아이템 CSS 클래스
   */
  itemClassName?: string;
}

export const defaultV2QsCollapsibleProps: Partial<V2QsCollapsibleProps> = {
  disabled: false,
  readOnly: false,
  hasError: false,
  loadingText: 'Loading...',
  addButtonText: 'Add',
  editButtonText: 'Edit',
};
